import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import Select from "react-select";
import DatePicker from "react-date-picker";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import Pagination from "../../Services/Pagination";
import Modal from "../../Services/Modal";
import { OpenModal, closeModal } from "../../Store/reducers/Common";
import { formatTakhmeenAmount } from "./takhmeenFormat";

const MEMBERS_PER_PAGE = 25;
const SUMMARY_MONTHS = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];
const PAYMENT_CYCLES = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
];

const selectMenuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: {
        container: (base) => ({ ...base, maxWidth: "calc(100% - 100px)" }),
        menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    },
};

const ViewTakhmeen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, accessToken } = useSelector((s) => s.user);
    const takhmeenId = location.state?.takhmeen_id;

    const [data, setData] = useState(null);
    const [userContext, setUserContext] = useState("");
    const [membersList, setMembersList] = useState([]);
    const [membersTotal, setMembersTotal] = useState(0);
    const [membersPage, setMembersPage] = useState(1);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [memberAssignment, setMemberAssignment] = useState(null);
    const [roles, setRoles] = useState([]);
    const [canAssign, setCanAssign] = useState(false);
    const [canReceipt, setCanReceipt] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [receiptMember, setReceiptMember] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [divisionMembers, setDivisionMembers] = useState([]);
    const [assignCoordinator, setAssignCoordinator] = useState(null);
    const [assignRows, setAssignRows] = useState([]);
    const [memberPicker, setMemberPicker] = useState([]);
    const [defaultAssignAmount, setDefaultAssignAmount] = useState("");
    const [allAssignedMembers, setAllAssignedMembers] = useState([]);
    const [assignDataLoading, setAssignDataLoading] = useState(false);
    const [assignmentSummary, setAssignmentSummary] = useState(null);
    const [memberSearch, setMemberSearch] = useState("");
    const skipDebouncedSearchRef = useRef(true);
    const [summaryFilterType, setSummaryFilterType] = useState("monthly");
    const [summaryMonth, setSummaryMonth] = useState(moment().month() + 1);
    const [summaryYear, setSummaryYear] = useState(moment().year());
    const [summaryDateFrom, setSummaryDateFrom] = useState(new Date());
    const [summaryDateTo, setSummaryDateTo] = useState(new Date());

    const summaryYearOptions = useMemo(() => {
        const currentYear = moment().year();
        return Array.from({ length: 6 }, (_, i) => currentYear - i);
    }, []);

    const coordinators = useMemo(() => roles.filter((r) => r.role_type === "coordinator"), [roles]);
    const financeIncharges = useMemo(() => roles.filter((r) => r.role_type === "finance_incharge"), [roles]);
    const projectIncharges = useMemo(() => roles.filter((r) => r.role_type === "project_incharge"), [roles]);

    const formatRoleName = (role) => {
        const name = role?.full_name?.trim();
        if (name) return name;
        if (role?.user_its) return `ITS ${role.user_its}`;
        return "-";
    };

    const formatRoleNames = (roleList) => (roleList.length ? roleList.map(formatRoleName).join(", ") : "-");

    const coordinatorOptions = useMemo(
        () => coordinators.map((c) => ({ value: c.user_id, label: c.full_name?.trim() || `ITS ${c.user_its}` })),
        [coordinators]
    );

    const divisionMemberOptions = useMemo(() => divisionMembers
        .filter((m) => {
            if (!assignCoordinator) return true;
            const assigned = allAssignedMembers.find((ml) => parseInt(ml.member_user_id, 10) === parseInt(m.user_id, 10));
            if (!assigned) return true;
            return parseInt(assigned.coordinator_user_id, 10) === parseInt(assignCoordinator.value, 10);
        })
        .map((m) => ({
            value: m.user_id,
            label: `${m.full_name?.trim()} (${m.user_its})`,
        })), [divisionMembers, allAssignedMembers, assignCoordinator]);

    const assignModalSummary = useMemo(() => {
        if (!data) return null;
        const base = parseFloat(data.base_amount || 0);
        const otherTotal = allAssignedMembers
            .filter((m) => !assignCoordinator || parseInt(m.coordinator_user_id, 10) !== parseInt(assignCoordinator.value, 10))
            .reduce((s, m) => s + parseFloat(m.takhmeen_amount || 0), 0);
        const currentTotal = assignRows.reduce((s, r) => s + parseFloat(r.takhmeen_amount || 0), 0);
        const totalAssigned = otherTotal + currentTotal;
        return {
            base_amount: base,
            total_assigned_amount: totalAssigned,
            remaining_to_assign: base - totalAssigned,
            assignment_completion_pct: base > 0 ? ((totalAssigned / base) * 100).toFixed(1) : "0.0",
        };
    }, [data, allAssignedMembers, assignCoordinator, assignRows]);

    const load = (opts = {}) => {
        if (!takhmeenId) return;
        const searchTerm = String(opts.search !== undefined ? opts.search : memberSearch).trim();
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/view-takhmeen", {
            takhmeen_id: takhmeenId,
            members_page: opts.page || membersPage,
            members_per_page: MEMBERS_PER_PAGE,
            member_user_id: opts.memberUserId || selectedMember?.member_user_id || 0,
            search: searchTerm,
        }, accessToken).then(({ data: res }) => {
            if (res.status === "1") {
                setData(res.data);
                setUserContext(res.userContext);
                setMembersList(res.membersList || []);
                setMembersTotal(res.membersTotal || 0);
                setMembersPage(res.membersPage || 1);
                setLedgerEntries(res.ledgerEntries || []);
                setMemberAssignment(res.memberAssignment || null);
                setRoles(res.roles || []);
                setCanAssign(!!res.canAssignMembers);
                setCanReceipt(!!res.canGenerateReceipt);
                setAssignmentSummary(res.assignmentSummary || null);
                if (res.userContext === "member" && res.memberAssignment) {
                    setSelectedMember({ ...res.memberAssignment, member_user_id: userData.id });
                }
            } else {
                dispatch(setToastData({ message: res.message, type: "error", showCloseBtn: true }));
                navigate("/takhmeen");
            }
        }).catch((e) => responseCatchFunc(dispatch, e)).finally(() => dispatch(setMainLoading(false)));
    };

    const runMemberSearch = (searchValue = memberSearch) => {
        const term = String(searchValue).trim();
        setMembersPage(1);
        setSelectedMember(null);
        load({ page: 1, search: term, memberUserId: 0 });
    };

    const clearMemberSearch = () => {
        skipDebouncedSearchRef.current = true;
        setMemberSearch("");
        setMembersPage(1);
        setSelectedMember(null);
        load({ page: 1, search: "", memberUserId: 0 });
    };

    useEffect(() => {
        if (!takhmeenId) { navigate("/takhmeen"); return; }
        dispatch(setPageTitle("View Takhmeen"));
        load();
    }, [takhmeenId]);

    useEffect(() => {
        if (!takhmeenId) return;
        if (skipDebouncedSearchRef.current) {
            skipDebouncedSearchRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            runMemberSearch(memberSearch);
        }, 400);
        return () => clearTimeout(timer);
    }, [memberSearch, takhmeenId]);

    const openLedger = (member) => {
        setSelectedMember(member);
        load({ memberUserId: member.member_user_id, page: membersPage });
        setTimeout(() => document.getElementById("takhmeen-member-ledger")?.scrollIntoView({ behavior: "smooth" }), 300);
    };

    const downloadFileResponse = (response, defaultFileName, validTypes) => {
        const blobPromise = response.blob();
        const headers = response.headers;
        return Promise.all([blobPromise, headers]).then(async ([blob, responseHeaders]) => {
            const contentType = responseHeaders.get("Content-Type") || "";
            const isValidType = !contentType || validTypes.some((type) => contentType.includes(type));
            if (!response.ok || !isValidType) {
                const message = await blob.text();
                throw new Error(message || "Failed to download file.");
            }
            const contentDisposition = responseHeaders.get("Content-Disposition");
            let fileName = defaultFileName;
            if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, "");
                }
            }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        });
    };

    const downloadPdfResponse = (response, defaultFileName) => downloadFileResponse(
        response,
        defaultFileName,
        ["application/pdf", "octet-stream"]
    );

    const exportMembersExcel = () => {
        dispatch(setMainLoading(true));
        const formData = new FormData();
        formData.append("token", accessToken);
        formData.append("takhmeen_id", takhmeenId);
        if (memberSearch.trim()) {
            formData.append("search", memberSearch.trim());
        }
        fetch(`${process.env.REACT_APP_API_URL}takhmeen/export-members-excel`, { method: "POST", body: formData, mode: "cors" })
            .then((response) => downloadFileResponse(
                response,
                `takhmeen-members-${takhmeenId}-${moment().format("YYYY-MM-DD-HHmmss")}.xlsx`,
                ["spreadsheetml", "octet-stream"]
            ))
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const buildReceiptsSummaryFormData = () => {
        const formData = new FormData();
        formData.append("token", accessToken);
        formData.append("takhmeen_id", takhmeenId);
        formData.append("filter_type", summaryFilterType);
        if (summaryFilterType === "monthly") {
            formData.append("month", summaryMonth);
            formData.append("year", summaryYear);
        } else {
            formData.append("date_from", moment(summaryDateFrom).format("YYYY-MM-DD"));
            formData.append("date_to", moment(summaryDateTo).format("YYYY-MM-DD"));
        }
        return formData;
    };

    const validateReceiptsSummaryPeriod = () => {
        if (summaryFilterType === "monthly") {
            return true;
        }
        if (!summaryDateFrom || !summaryDateTo) {
            dispatch(setToastData({ message: "Select both from and to dates.", type: "error", showCloseBtn: true }));
            return false;
        }
        if (moment(summaryDateFrom).isAfter(moment(summaryDateTo), "day")) {
            dispatch(setToastData({ message: "From date cannot be after to date.", type: "error", showCloseBtn: true }));
            return false;
        }
        return true;
    };

    const exportReceiptsSummaryExcel = () => {
        if (!validateReceiptsSummaryPeriod()) return;
        dispatch(setMainLoading(true));
        fetch(`${process.env.REACT_APP_API_URL}takhmeen/export-receipts-summary-excel`, {
            method: "POST",
            body: buildReceiptsSummaryFormData(),
            mode: "cors",
        })
            .then((response) => downloadFileResponse(
                response,
                `takhmeen-receipts-summary-${takhmeenId}-${moment().format("YYYY-MM-DD-HHmmss")}.xlsx`,
                ["spreadsheetml", "octet-stream"]
            ))
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const exportReceiptsSummaryPdf = () => {
        if (!validateReceiptsSummaryPeriod()) return;
        dispatch(setMainLoading(true));
        fetch(`${process.env.REACT_APP_API_URL}takhmeen/export-receipts-summary-pdf`, {
            method: "POST",
            body: buildReceiptsSummaryFormData(),
            mode: "cors",
        })
            .then((response) => downloadPdfResponse(response, `takhmeen-receipts-summary-${takhmeenId}-${moment().format("YYYY-MM-DD-HHmmss")}.pdf`))
            .catch((e) => {
                const message = e?.message && e.message !== "Failed to fetch" ? e.message : "Unable to download receipts summary PDF.";
                dispatch(setToastData({ message, type: "error", showCloseBtn: true }));
            })
            .finally(() => dispatch(setMainLoading(false)));
    };

    const exportLedgerPdf = () => {
        const memId = selectedMember?.member_user_id || userData.id;
        const formData = new FormData();
        formData.append("token", accessToken);
        formData.append("takhmeen_id", takhmeenId);
        formData.append("mem_id", memId);
        fetch(`${process.env.REACT_APP_API_URL}takhmeen/export-member-ledger-pdf`, { method: "POST", body: formData, mode: "cors" })
            .then((response) => downloadPdfResponse(response, `takhmeen-ledger-${takhmeenId}-${memId}.pdf`))
            .catch((e) => {
                const message = e?.message && e.message !== "Failed to fetch" ? e.message : "Unable to download ledger PDF.";
                dispatch(setToastData({ message, type: "error", showCloseBtn: true }));
            });
    };

    const downloadReceiptPdf = (receiptId) => {
        const formData = new FormData();
        formData.append("token", accessToken);
        formData.append("receipt_id", receiptId);
        fetch(`${process.env.REACT_APP_API_URL}takhmeen/download-receipt-pdf`, { method: "POST", body: formData, mode: "cors" })
            .then((response) => downloadPdfResponse(response, `receipt-${receiptId}.pdf`))
            .catch((e) => {
                const message = e?.message && e.message !== "Failed to fetch" ? e.message : "Unable to download receipt PDF.";
                dispatch(setToastData({ message, type: "error", showCloseBtn: true }));
            });
    };

    const closeAssign = () => {
        setAssignOpen(false);
        setAssignCoordinator(null);
        setAssignRows([]);
        setMemberPicker([]);
        setDefaultAssignAmount("");
        setAllAssignedMembers([]);
        setAssignDataLoading(false);
        dispatch(closeModal());
    };

    const loadAllAssignedMembers = async () => {
        const all = [];
        let page = 1;
        let total = 0;
        do {
            const viewRes = await PostAxios("takhmeen/view-takhmeen", {
                takhmeen_id: takhmeenId,
                members_page: page,
                members_per_page: 5000,
                member_user_id: 0,
                for_assign: 1,
            }, accessToken);
            if (viewRes.data.status !== "1") break;
            const batch = viewRes.data.membersList || [];
            total = viewRes.data.membersTotal ?? batch.length;
            all.push(...batch);
            page += 1;
        } while (all.length < total);
        return all;
    };

    const openAssign = () => {
        if (!data) return;
        setDefaultAssignAmount(data.base_amount || "");
        setAssignCoordinator(null);
        setAssignRows([]);
        setMemberPicker([]);
        setAllAssignedMembers([]);
        setAssignDataLoading(true);
        dispatch(setMainLoading(true));
        Promise.all([
            PostAxios("takhmeen/division-members", { org_id: data.org_id, div_id: data.div_id }, accessToken),
            loadAllAssignedMembers(),
        ]).then(([divRes, assignedMembers]) => {
            if (divRes.data.status === "1") setDivisionMembers(divRes.data.list || []);
            setAllAssignedMembers(assignedMembers);
        }).finally(() => {
            setAssignDataLoading(false);
            dispatch(setMainLoading(false));
        });
        setAssignOpen(true);
        dispatch(OpenModal());
    };

    const onCoordinatorChange = (opt) => {
        setAssignCoordinator(opt);
        if (!opt) {
            setAssignRows([]);
            setMemberPicker([]);
            return;
        }
        const existing = allAssignedMembers.filter(
            (m) => parseInt(m.coordinator_user_id, 10) === parseInt(opt.value, 10)
        );
        const rows = existing.map((m) => ({
            user_id: m.member_user_id,
            member_name: m.member_name?.trim(),
            user_its: m.user_its,
            takhmeen_amount: m.takhmeen_amount,
            payment_cycle: m.payment_cycle || "monthly",
        }));
        setAssignRows(rows);
        setMemberPicker(rows.map((r) => ({
            value: r.user_id,
            label: `${r.member_name} (${r.user_its})`,
        })));
    };

    const handleMemberPickerChange = (selected) => {
        const selectedOpts = selected || [];
        setMemberPicker(selectedOpts);
        const rows = selectedOpts.map((opt) => {
            const existing = assignRows.find((r) => String(r.user_id) === String(opt.value));
            if (existing) return existing;
            const m = divisionMembers.find((dm) => String(dm.user_id) === String(opt.value));
            const fromList = allAssignedMembers.find((ml) => String(ml.member_user_id) === String(opt.value));
            return {
                user_id: opt.value,
                member_name: m?.full_name?.trim() || opt.label,
                user_its: m?.user_its || "",
                takhmeen_amount: fromList?.takhmeen_amount ?? 0 ?? "",
                payment_cycle: fromList?.payment_cycle ?? "monthly",
            };
        });
        setAssignRows(rows);
    };

    const updateAssignRow = (userId, field, value) => {
        setAssignRows(assignRows.map((r) => (String(r.user_id) === String(userId) ? { ...r, [field]: value } : r)));
    };

    const removeAssignRow = (userId) => {
        setAssignRows(assignRows.filter((r) => String(r.user_id) !== String(userId)));
        setMemberPicker(memberPicker.filter((p) => String(p.value) !== String(userId)));
    };

    const applyDefaultAmount = () => {
        if (!defaultAssignAmount || parseFloat(defaultAssignAmount) <= 0) {
            dispatch(setToastData({ message: "Enter a valid default amount.", type: "error", showCloseBtn: true }));
            return;
        }
        setAssignRows(assignRows.map((r) => ({ ...r, takhmeen_amount: defaultAssignAmount })));
    };

    const submitAssign = () => {
        if (!assignCoordinator) {
            dispatch(setToastData({ message: "Select a coordinator.", type: "error", showCloseBtn: true }));
            return;
        }
        const members = assignRows.filter((r) => r.user_id).map((r) => ({
            user_id: parseInt(r.user_id, 10),
            takhmeen_amount: parseFloat(r.takhmeen_amount),
            payment_cycle: r.payment_cycle,
        }));
        if (members.length && members.some((m) => !m.takhmeen_amount || m.takhmeen_amount <= 0)) {
            dispatch(setToastData({ message: "Each member must have a valid takhmeen amount.", type: "error", showCloseBtn: true }));
            return;
        }
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/assign-members", {
            takhmeen_id: takhmeenId,
            coordinator_user_id: assignCoordinator.value,
            members: JSON.stringify(members),
        }, accessToken).then(({ data: res }) => {
            if (res.status === "1") {
                dispatch(setToastData({ message: res.message, type: "success", showCloseBtn: true }));
                closeAssign();
                load();
            } else dispatch(setToastData({ message: res.message, type: "error", showCloseBtn: true }));
        }).finally(() => dispatch(setMainLoading(false)));
    };

    const submitReceipt = (values, { resetForm, setSubmitting }) => {
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/generate-receipt", {
            takhmeen_id: takhmeenId,
            mem_id: receiptMember.member_user_id,
            amount: values.amount,
            receipt_date: moment(values.receipt_date).format("YYYY-MM-DD"),
            payment_mode: values.payment_mode,
            transaction_id: values.transaction_id,
            payment_app: values.payment_app,
            remarks: values.remarks,
        }, accessToken).then(({ data: res }) => {
            if (res.status === "1") {
                dispatch(setToastData({ message: res.message, type: "success", showCloseBtn: true }));
                resetForm();
                setReceiptMember(null);
                dispatch(closeModal());
                load({ memberUserId: receiptMember.member_user_id });
            } else dispatch(setToastData({ message: res.errors ? ConvertErrorArrayToString(res.errors) : res.message, type: "error", showCloseBtn: true }));
        }).finally(() => { setSubmitting(false); dispatch(setMainLoading(false)); });
    };

    if (!data) return null;

    const isMemberView = userContext === "member";
    const isCoordinatorView = userContext === "coordinator";

    return (
        <>
            <div className="main-form-sec">
                <div className="form-sec">
                    <div className="d-flex justify-content-between align-items-center w-100 px-3">
                        <h4 className="mb-3 w-50">{data.takhmeen_name}</h4>
                        <div className="top-right-btn p-0 w-50">
                            {!isMemberView && canAssign && (
                                <button type="button" className="btn btn-blue br-0 me-2" onClick={openAssign}>Assign Members</button>
                            )}
                            <button type="button" className="btn btn-blue br-0" onClick={() => navigate("/takhmeen")}>Back</button>
                        </div>
                    </div>



                    <div className="w-50 p-3">Division : <b>{data.division_name}</b></div>
                    <div className="w-50 p-3">Project Incharge : <b>{formatRoleNames(projectIncharges)}</b></div>
                    <div className="w-50 p-3">Finance Incharge : <b>{formatRoleNames(financeIncharges)}</b></div>
                    {!isMemberView && !isCoordinatorView && (
                        <div className="w-50 p-3">Takhmeen Amount : <b>{formatTakhmeenAmount(data.base_amount)}</b></div>
                    )}
                    {isCoordinatorView && assignmentSummary && (
                        <div className="w-50 p-3">Assigned Takhmeen Amount : <b>{formatTakhmeenAmount(assignmentSummary.total_assigned_amount)}</b></div>
                    )}
                    <div className="w-50 p-3">Period : <b>{moment(data.start_date).format("DD MMM YYYY")} - {moment(data.end_date).format("DD MMM YYYY")}</b></div>
                    <div className="w-50 p-3">Description : <b>{data.description || "-"}</b></div>
                </div>

                {!isMemberView && assignmentSummary && (<>
                    <hr className="my-3 w-100" />
                    <div className="form-sec mt-0 pt-0">
                        <h4 className="px-3 mb-2">{isCoordinatorView ? "My Members Progress" : "Takhmeen Progress"}</h4>
                        <div className="d-flex flex-wrap w-100">
                            {isCoordinatorView ? (
                                <>
                                    <div className="w-50 p-3">Assigned Takhmeen Amount : <b>{formatTakhmeenAmount(assignmentSummary.total_assigned_amount)}</b></div>
                                    <div className="w-50 p-3">Amount Collected : <b>{formatTakhmeenAmount(assignmentSummary.total_collected_amount)}</b></div>
                                    <div className="w-50 p-3">Members Assigned : <b>{assignmentSummary.assigned_member_count}</b></div>
                                    <div className="w-50 p-3">Collection Remaining : <b>{formatTakhmeenAmount(assignmentSummary.collection_remaining)}</b></div>
                                    <div className="w-50 p-3">Collection Completion : <b>{assignmentSummary.collection_completion_pct}%</b></div>
                                </>
                            ) : (
                                <>
                                    <div className="w-50 p-3">Base Takhmeen Amount : <b>{formatTakhmeenAmount(assignmentSummary.base_amount)}</b></div>
                                    <div className="w-50 p-3">Assigned via Members : <b>{formatTakhmeenAmount(assignmentSummary.total_assigned_amount)}</b></div>
                                    <div className="w-50 p-3">Amount Collected : <b>{formatTakhmeenAmount(assignmentSummary.total_collected_amount)}</b></div>
                                    <div className="w-50 p-3">
                                        Remaining to Assign :
                                        <b className={parseFloat(assignmentSummary.remaining_to_assign) > 0 ? " text-warning" : parseFloat(assignmentSummary.remaining_to_assign) < 0 ? " text-danger" : " text-success"}>
                                            {" "}{formatTakhmeenAmount(assignmentSummary.remaining_to_assign)}
                                        </b>
                                    </div>
                                    <div className="w-50 p-3">Members Assigned : <b>{assignmentSummary.assigned_member_count}</b></div>
                                    <div className="w-50 p-3">Assignment Coverage : <b>{assignmentSummary.assignment_completion_pct}%</b></div>
                                    {parseFloat(assignmentSummary.remaining_to_assign) > 0 && (
                                        <div className="w-100 px-3 pb-3 text-muted" style={{ fontSize: 14 }}>
                                            {formatTakhmeenAmount(assignmentSummary.remaining_to_assign)} still needs to be distributed when assigning new members.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
                )}

                {!isMemberView && (
                    <>
                        <hr className="my-3 w-100" />
                        <div className="form-sec mt-0 pt-0 px-3 pb-3">
                            <h4 className="mb-3">Receipts Summary</h4>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="form-field-group mb-0" style={{ minWidth: 180 }}>
                                    <label>Report Type</label>
                                    <select
                                        className="select-input w-100"
                                        value={summaryFilterType}
                                        onChange={(e) => setSummaryFilterType(e.target.value)}
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="date_range">Date Range</option>
                                    </select>
                                </div>
                                {summaryFilterType === "monthly" ? (
                                    <>
                                        <div className="form-field-group mb-0" style={{ minWidth: 160 }}>
                                            <label>Month</label>
                                            <select
                                                className="select-input w-100"
                                                value={summaryMonth}
                                                onChange={(e) => setSummaryMonth(parseInt(e.target.value, 10))}
                                            >
                                                {SUMMARY_MONTHS.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-field-group mb-0" style={{ minWidth: 120 }}>
                                            <label>Year</label>
                                            <select
                                                className="select-input w-100"
                                                value={summaryYear}
                                                onChange={(e) => setSummaryYear(parseInt(e.target.value, 10))}
                                            >
                                                {summaryYearOptions.map((year) => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="form-field-group mb-0" style={{ minWidth: 180 }}>
                                            <label>From Date</label>
                                            <DatePicker
                                                value={summaryDateFrom}
                                                onChange={(d) => setSummaryDateFrom(d)}
                                                format="dd/MM/yyyy"
                                                maxDate={summaryDateTo || new Date()}
                                                className="form-input"
                                            />
                                        </div>
                                        <div className="form-field-group mb-0" style={{ minWidth: 180 }}>
                                            <label>To Date</label>
                                            <DatePicker
                                                value={summaryDateTo}
                                                onChange={(d) => setSummaryDateTo(d)}
                                                format="dd/MM/yyyy"
                                                minDate={summaryDateFrom}
                                                maxDate={new Date()}
                                                className="form-input"
                                            />
                                        </div>
                                    </>
                                )}
                                <button type="button" className="btn btn-sm btn-blue" onClick={exportReceiptsSummaryExcel}>
                                    <i className="fas fa-download" /> Export Excel
                                </button>
                                <button type="button" className="btn btn-sm btn-blue ms-2" onClick={exportReceiptsSummaryPdf}>
                                    <i className="fas fa-file-pdf" /> Export PDF
                                </button>
                            </div>
                            <small className="text-muted d-block mt-2">
                                Download receipt summary with member name, ITS, payment mode, transaction date, UPI/UTR, and amount.
                            </small>
                        </div>
                    </>
                )}
            </div>

            {isMemberView && memberAssignment && (
                <div className="main-form-sec form-sec mx-0">
                    <h4 className="mb-3 w-100">My Takhmeen</h4>
                    <div className="w-50 pb-3">Takhmeen Amount : <b>{formatTakhmeenAmount(memberAssignment.takhmeen_amount)}</b></div>
                    <div className="w-50 pb-3">Coordinator : <b>{memberAssignment.coordinator_name}</b></div>
                    <div className="w-50 pb-3">Payment Cycle : <b>{memberAssignment.payment_cycle}</b></div>
                    <div className="w-50 pb-3">Amount Paid : <b>{formatTakhmeenAmount(memberAssignment.amount_paid || 0)}</b></div>
                    <div className="w-50 pb-3">Completion : <b>{((parseFloat(memberAssignment.amount_paid || 0) / parseFloat(memberAssignment.takhmeen_amount)) * 100).toFixed(1)}%</b></div>
                    <button type="button" className="btn btn-blue ms-3" onClick={exportLedgerPdf}>Export PDF</button>
                </div>
            )}

            {!isMemberView && (
                <div className="main-form-sec">
                    <div className="form-sec d-flex flex-wrap gap-3 justify-content-between align-items-end ps-0 pe-3 mb-3">
                        <div className="d-flex flex-wrap gap-3 align-items-center">
                            <div className="form-field-group mb-0" style={{flexWrap:'nowrap' }}>
                                <label style={{display:'inline-block',whiteSpace:'nowrap'}}>Search Member</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Name or ITS..."
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                />
                            </div>
                            {memberSearch.trim() && (
                                <button type="button" className="btn btn-blue" style={{ background: "#6c757d", borderColor: "#6c757d" }} onClick={clearMemberSearch}>
                                    Clear
                                </button>
                            )}
                            {memberSearch.trim() && <div className="form-field-group mb-0"  style={{ }}>
                                <label></label>
                                <span className="text-muted align-self-center" style={{ fontSize: 14 }}>
                                    {membersTotal} member{membersTotal === 1 ? "" : "s"} found
                                </span>

                            </div>}
                        </div>
                        <button type="button" className="btn btn-sm btn-blue ms-auto" onClick={exportMembersExcel}>
                            <i className="fas fa-download" /> Export to Excel
                        </button>
                    </div>
                    <div className="table-main">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Member Name</th>
                                    <th>ITS</th>
                                    <th>Coordinator</th>
                                    <th>Takhmeen Amount</th>
                                    <th>Amount Received</th>
                                    <th>Percentage</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!membersList.length && (
                                    <tr>
                                        <td colSpan={8} className="text-center text-muted py-4">
                                            {memberSearch.trim() ? "No members found for this search." : "No members assigned."}
                                        </td>
                                    </tr>
                                )}
                                {membersList.map((m, i) => (
                                    <tr key={m.member_user_id}>
                                        <td>{(membersPage - 1) * MEMBERS_PER_PAGE + i + 1}</td>
                                        <td>{m.member_name?.trim()}</td>
                                        <td>{m.user_its}</td>
                                        <td>{m.coordinator_name?.trim()}</td>
                                        <td>{formatTakhmeenAmount(m.takhmeen_amount)}</td>
                                        <td>{formatTakhmeenAmount(m.amount_paid || 0)}</td>
                                        <td>{m.completion_percentage ?? ((parseFloat(m.amount_paid || 0) / parseFloat(m.takhmeen_amount)) * 100).toFixed(1)}%</td>
                                        <td>
                                            <button type="button" className="btn btn-sm btn-blue me-1" onClick={() => openLedger(m)}>Ledger</button>
                                            {canReceipt && parseInt(data.status, 10) === 1 && (
                                                <button type="button" className="btn btn-sm status-btn approved" style={{ float: "none" }} onClick={() => { setReceiptMember(m); dispatch(OpenModal()); }}>Generate Receipt</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={membersPage} totalCount={membersTotal} pageSize={MEMBERS_PER_PAGE}
                        onPageChange={(p) => { setMembersPage(p); load({ page: p, search: memberSearch }); }} />
                </div>
            )}

            {(selectedMember || (isMemberView && memberAssignment)) && (
                <div id="takhmeen-member-ledger" className="main-form-sec">
                    <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                        <h4 className="mb-0">Member Ledger</h4>
                        <div>
                            <button type="button" className="btn btn-sm btn-blue me-2" onClick={exportLedgerPdf}>Export PDF</button>
                            {!isMemberView && (
                                <button type="button" className="btn btn-sm btn-blue" onClick={() => { setSelectedMember(null); load({ memberUserId: 0 }); }}><i className="fas fa-times" /> Close</button>
                            )}
                        </div>
                    </div>
                    <div className="table-main">
                        <table className="table">
                            <thead><tr><th>Date</th><th>Receipt No.</th><th>Amount</th><th>Mode</th><th>Transaction</th><th>Remarks</th><th>Action</th></tr></thead>
                            <tbody>
                                {!ledgerEntries.length && <tr><td colSpan={7} className="text-center text-muted py-4">No receipts yet.</td></tr>}
                                {ledgerEntries.map((e) => (
                                    <tr key={e.id}>
                                        <td>{moment(e.receipt_date).format("DD MMM YYYY")}</td>
                                        <td>{e.receipt_number}</td>
                                        <td>{formatTakhmeenAmount(e.amount)}</td>
                                        <td>{e.payment_mode}</td>
                                        <td>{e.transaction_id || "-"}</td>
                                        <td>{e.remarks || "-"}</td>
                                        <td>
                                            <button type="button" className="btn btn-sm btn-blue" onClick={() => downloadReceiptPdf(e.id)}>Receipt</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {receiptMember && (
                <Modal classList="popup-main" style={{ maxWidth: 600, width: "100%" }} onClose={() => { setReceiptMember(null); dispatch(closeModal()); }}>
                    <h4 className="mb-3">Generate Receipt</h4>
                    <Formik initialValues={{ amount: "", receipt_date: new Date(), payment_mode: "cash", transaction_id: "", payment_app: "", remarks: "" }}
                        validate={(v) => {
                            const e = {};
                            if (!v.amount || parseFloat(v.amount) <= 0) e.amount = "Required";
                            if (v.payment_mode === "online" && !v.transaction_id.trim()) e.transaction_id = "Required for online";
                            return e;
                        }}
                        onSubmit={submitReceipt}
                    >
                        {({ values, setFieldValue, isSubmitting }) => (
                            <Form>
                                <div className="form-sec mx-0">
                                    <div className="form-field-group w-100 mb-2 px-0"><label>Member</label><input className="form-input" readOnly value={receiptMember.member_name?.trim()} /></div>
                                    <div className="form-field-group w-100 mb-2 px-0"><label>Takhmeen Amount</label><input className="form-input" readOnly value={formatTakhmeenAmount(receiptMember.takhmeen_amount)} /></div>
                                    <div className="form-field-group w-100 mb-2 px-0"><label>Amount*</label><Field name="amount" type="number" min="1" step="1" onWheel={(e) => e.currentTarget.blur()} className="form-input" /></div>
                                    <div className="form-field-group w-100 mb-2 px-0"><label>Receipt Date*</label>
                                        <DatePicker value={values.receipt_date} onChange={(d) => setFieldValue("receipt_date", d)} format="dd/MM/yyyy" maxDate={new Date()} className="form-input" />
                                    </div>
                                    <div className="form-field-group w-100 mb-2 px-0">
                                        <label className="select-input-label">Payment Mode*</label>
                                        <Field as="select" name="payment_mode" className="select-input w-100"><option value="cash">Cash</option><option value="online">Online</option></Field>
                                    </div>
                                    {values.payment_mode === "online" && (
                                        <>
                                            <div className="form-field-group w-100 mb-2 px-0"><label>Transaction ID*</label><Field name="transaction_id" className="form-input" /></div>
                                            <div className="form-field-group w-100 mb-2 px-0"><label>Payment App</label><Field name="payment_app" className="form-input" placeholder="GPay, PhonePe, etc." /></div>
                                        </>
                                    )}
                                    <div className="form-field-group w-100 mb-3 px-0"><label>Remarks</label><Field name="remarks" className="form-input form-textarea" as="textarea" rows={3} /></div>
                                </div>
                                <button type="submit" className="btn btn-blue" disabled={isSubmitting}>Save Receipt</button>
                            </Form>
                        )}
                    </Formik>
                </Modal>
            )}

            {assignOpen && (
                <Modal classList="popup-main" style={{ maxWidth: 920, width: "100%" }} onClose={closeAssign}>
                    <h4 className="mb-1">Assign Members to Coordinator</h4>
                    <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                        Choose a coordinator, select members from the division, then set each member&apos;s takhmeen amount and payment cycle.
                    </p>

                    <div className="form-field-group w-100 mb-4 ps-0 justify-content-start">
                        <label className="mb-1" style={{ width: 100 }}>Coordinator *</label>
                        <Select
                            options={coordinatorOptions}
                            value={assignCoordinator}
                            onChange={onCoordinatorChange}
                            placeholder={assignDataLoading ? "Loading assignments..." : "Search and select coordinator..."}
                            isDisabled={assignDataLoading}
                            isClearable
                            {...selectMenuPortalProps}
                        />
                    </div>

                    {assignCoordinator && (
                        <>
                            <div className="form-field-group w-100 mb-4 ps-0 justify-content-start">
                                <label className="mb-1" style={{ width: 100 }}>Members * </label>
                                <Select
                                    isMulti
                                    options={divisionMemberOptions}
                                    value={memberPicker}
                                    onChange={handleMemberPickerChange}
                                    placeholder="Search by name or ITS..."
                                    closeMenuOnSelect={false}
                                    {...selectMenuPortalProps}
                                />
                            </div>

                            {assignModalSummary && (
                                <div className="d-flex flex-wrap gap-3 mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: 4 }}>
                                    <span className="w-50 mb-2">Base Amount: <b>{formatTakhmeenAmount(assignModalSummary.base_amount)}</b></span>
                                    <span className="w-50 mb-2">Assigned (incl. below): <b>{formatTakhmeenAmount(assignModalSummary.total_assigned_amount)}</b></span>
                                    <span className="w-50 mb-2">
                                        Remaining to Assign:{" "}
                                        <b className={assignModalSummary.remaining_to_assign > 0 ? "text-warning" : assignModalSummary.remaining_to_assign < 0 ? "text-danger" : "text-success"}>
                                            {formatTakhmeenAmount(assignModalSummary.remaining_to_assign)}
                                        </b>
                                    </span>
                                    <span className="w-50 mb-2">Coverage: <b>{assignModalSummary.assignment_completion_pct}%</b></span>
                                </div>
                            )}

                            {assignRows.length > 0 && (
                                <>
                                    <div className="table-main mb-3">
                                        <table className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 40 }}>#</th>
                                                    <th>Member Name</th>
                                                    <th style={{ width: 100 }}>ITS</th>
                                                    <th style={{ width: 150 }}>Takhmeen Amount *</th>
                                                    <th style={{ width: 140 }}>Payment Cycle *</th>
                                                    <th style={{ width: 70 }}>Remove</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assignRows.map((row, idx) => (
                                                    <tr key={row.user_id}>
                                                        <td>{idx + 1}</td>
                                                        <td>{row.member_name}</td>
                                                        <td>{row.user_its}</td>
                                                        <td>
                                                            <div className="form-field-group w-100 mb-0">
                                                                <input
                                                                    style={{ height: 34 }}
                                                                    type="number"
                                                                    min="1"
                                                                    step="1"
                                                                    className="form-input"
                                                                    value={row.takhmeen_amount}
                                                                    placeholder="Enter Takhmeen Amount"
                                                                    onChange={(e) => updateAssignRow(row.user_id, "takhmeen_amount", e.target.value)}
                                                                    onWheel={(e) => e.currentTarget.blur()}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="select-input w-100"
                                                                value={row.payment_cycle}
                                                                onChange={(e) => updateAssignRow(row.user_id, "payment_cycle", e.target.value)}
                                                            >
                                                                {PAYMENT_CYCLES.map((c) => (
                                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm status-btn rejected"
                                                                style={{ float: "none" }}
                                                                title="Remove member"
                                                                onClick={() => removeAssignRow(row.user_id)}
                                                            >
                                                                <i className="fas fa-times" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {!assignRows.length && (
                                <p className="text-muted text-center py-3 mb-0">No members selected yet. Use the search box above to add members.</p>
                            )}
                        </>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-4 py-3 border-top">
                        <button type="button" className="btn btn-blue" style={{ background: "#6c757d", borderColor: "#6c757d" }} onClick={closeAssign}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-blue ms-2" onClick={submitAssign} disabled={!assignCoordinator || assignDataLoading}>
                            Save Assignments
                        </button>
                    </div>
                </Modal>
            )}

        </>
    );
};

export default ViewTakhmeen;
